import { Box, Tab, Tabs, useMediaQuery, useTheme } from "@mui/material"
import { ContentWrapper, SnackbarProvider, SnackbarType, useAbortControllers, useTask } from "vig-ui-library";
import React, { useEffect, useRef, useState } from "react";
import { MyAssembliesList } from "@/Components/Assemblies/MyAssembliesList";
import { GetMyReportsExecutionHistoryInput, GetMyReportsExecutionHistoryInputSchema } from "@/Schemas/Reports/GetMyReportsExecutionHistoryInput";
import { getMyReportsExecutionHistory } from "@/Api/ReportApi";
import { GetMyReportsExecutionHistoryOutput, GetMyReportsExecutionHistoryOutputSchema } from "@/Schemas/Reports/GetMyReportsExecutionHistoryOutput";
import { MyAssembliesInbox, MyAssembliesInboxProps } from "@/Components/Assemblies/MyAssembliesInbox";

enum AssemblyTab {
    MY_ASSEMBLIES = 0,
    MY_ASSEMBLIES_INBOX = 1,
}

export const Assemblies = () => {
    const theme = useTheme();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const bigView = useMediaQuery(`(min-width:${920 + 360 + theme.variables.drawerWidth}px)`);

    const [tab, setTab] = useState<AssemblyTab>(AssemblyTab.MY_ASSEMBLIES_INBOX);

    const [executionQuery, setExecutionQuery] = useState<GetMyReportsExecutionHistoryInput>(
        GetMyReportsExecutionHistoryInputSchema.parse({})
    );

    const { controlledCall, abortAll } = useAbortControllers();

    const timeoutRef = useRef<number | null>(null);

    const getExecutionHistoryTask = useTask<[], GetMyReportsExecutionHistoryOutput>(async () => {
        try {
            if (typeof timeoutRef.current === "number") {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            const response = await controlledCall(async (controller) => await getMyReportsExecutionHistory(executionQuery, { signal: controller.signal }));

            const someAssemblyIsRunning = response.items.some((item) => !item.executionEndTime);

            if (someAssemblyIsRunning) {
                timeoutRef.current = setTimeout(() => getExecutionHistoryTask.perform(), 6000);
            }

            return response;
        } catch (err: any) {
            if (err.code !== "ERR_CANCELED") {
                openSnackbar({
                    text: "Nepodarilo sa načítať históriu zostáv, skúste to znova. Ak problém pretrváva, kontaktujte IT.",
                    type: SnackbarType.ERROR,
                });
            }
            return GetMyReportsExecutionHistoryOutputSchema.parse({ items: [], count: 0 });
        }
    });

    const inboxProps: MyAssembliesInboxProps = {
        refreshExecutionHistoryTask: getExecutionHistoryTask,
        query: executionQuery,
        setQuery: setExecutionQuery,
    }

    useEffect(() => {
        return () => {
            abortAll();
            if (typeof timeoutRef.current === "number") clearTimeout(timeoutRef.current);
        }
    }, []);

    return (
        <ContentWrapper>
            <Box sx={{
                display: "flex",
                flexDirection: bigView ? "row" : "column",
                alignItems: "flex-start",
                height: "100%",
            }}>
                {bigView && (
                    <>
                        <MyAssembliesList bigView={true} refreshExecutionHistoryTask={getExecutionHistoryTask} />

                        <MyAssembliesInbox {...inboxProps} />
                    </>
                )}

                {!bigView && (
                    <>
                        <AssembliesTabs tab={tab} handleTabChange={(_event, newValue) => setTab(newValue)} />
                        {tab === AssemblyTab.MY_ASSEMBLIES && <MyAssembliesList
                            bigView={false}
                            refreshExecutionHistoryTask={getExecutionHistoryTask}
                        />}
                        {tab === AssemblyTab.MY_ASSEMBLIES_INBOX && <MyAssembliesInbox {...inboxProps} />}
                    </>
                )}
            </Box>
        </ContentWrapper>
    );
}

type AssembliesTabsProps = {
    tab: AssemblyTab;
    handleTabChange: (_event: React.SyntheticEvent<Element, Event>, newValue: AssemblyTab) => void;
}

const AssembliesTabs = ({ tab, handleTabChange }: AssembliesTabsProps) => {
    return (
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0, width: "100%" }}>
            <Tabs
                value={tab}
                onChange={handleTabChange}
            >
                <Tab label="Moja Schránka zostáv" value={AssemblyTab.MY_ASSEMBLIES_INBOX} />
                <Tab label="Moje zostavy" value={AssemblyTab.MY_ASSEMBLIES} />
            </Tabs>
        </Box>
    );
};

